import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Patient } from "common/models/types";
import Input from "components/atoms/Input";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";

interface PatientInfoEditFormProps {
  patient: Patient;
  onSave: (patient: Patient) => void;
  onCancel: () => void;
}

const FormContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border-radius: 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 12px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
    box-shadow: 0 0 0 2px ${COLOR_SYSTEM.get("Blue")?.[100]};
  }
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 8px;
`;

const PatientInfoEditForm: React.FC<PatientInfoEditFormProps> = ({ patient, onSave, onCancel }) => {
  // 편집 가능한 환자 정보 상태
  const [editedPatient, setEditedPatient] = useState<Patient>({ ...patient });

  // 환자 정보가 변경될 때 상태 초기화
  useEffect(() => {
    setEditedPatient({ ...patient });
  }, [patient]);

  // 환자 정보 수정 핸들러
  const handleChange = (field: keyof Patient, value: any) => {
    setEditedPatient((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // 취소 확인
  const handleCancelConfirm = () => {
    const hasChanges = JSON.stringify(patient) !== JSON.stringify(editedPatient);

    if (hasChanges) {
      ConfirmManager.show({
        title: "변경 사항 취소",
        body: "변경 사항이 저장되지 않습니다. 계속하시겠습니까?",
        buttons: [
          <Button
            key="confirm"
            buttonType="button"
            color="Negative"
            size="Small"
            type="Text"
            variant="Contained"
            onClick={() => {
              onCancel();
              ConfirmManager.hide();
            }}
          >
            취소
          </Button>,
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Small"
            type="Text"
            variant="Outlined"
            onClick={() => ConfirmManager.hide()}
          >
            계속 편집
          </Button>,
        ],
      });
    } else {
      onCancel();
    }
  };

  return (
    <FormContainer>
      <div style={{ marginBottom: "16px" }}>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Blue")?.[700]}>
          환자 정보 수정
        </Text>
      </div>

      <FormSection>
        <Text textType="Subhead1">기본 정보</Text>
        <FormGrid>
          <Input
            label="생년월일"
            type="date"
            value={
              editedPatient.birthDate
                ? new Date(editedPatient.birthDate).toISOString().split("T")[0]
                : ""
            }
            onChange={(e) =>
              handleChange("birthDate", e.target.value ? new Date(e.target.value) : undefined)
            }
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "14px", marginBottom: "8px" }}>성별</label>
            <div style={{ display: "flex", gap: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="gender"
                  value="M"
                  checked={editedPatient.gender === "M"}
                  onChange={() => handleChange("gender", "M")}
                  style={{ marginRight: "8px" }}
                />
                남성
              </label>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="gender"
                  value="F"
                  checked={editedPatient.gender === "F"}
                  onChange={() => handleChange("gender", "F")}
                  style={{ marginRight: "8px" }}
                />
                여성
              </label>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "14px", marginBottom: "8px" }}>임신 여부</label>
            <div style={{ display: "flex", gap: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="pregnancyStatus"
                  value="true"
                  checked={editedPatient.pregnancyStatus === true}
                  onChange={() => handleChange("pregnancyStatus", true)}
                  style={{ marginRight: "8px" }}
                  disabled={editedPatient.gender !== "F"}
                />
                예
              </label>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="pregnancyStatus"
                  value="false"
                  checked={editedPatient.pregnancyStatus === false}
                  onChange={() => handleChange("pregnancyStatus", false)}
                  style={{ marginRight: "8px" }}
                />
                아니오
              </label>
            </div>
          </div>
        </FormGrid>
      </FormSection>

      <FormSection>
        <Text textType="Subhead1">의료 정보</Text>
        <FormItem>
          <Label>질병명</Label>
          <TextArea
            value={editedPatient.diseases || ""}
            onChange={(e) => handleChange("diseases", e.target.value)}
            placeholder="질병명을 입력하세요"
          />
        </FormItem>
        <FormItem>
          <Label>알러지 정보</Label>
          <TextArea
            value={editedPatient.allergies || ""}
            onChange={(e) => handleChange("allergies", e.target.value)}
            placeholder="알러지 정보를 입력하세요"
          />
        </FormItem>
      </FormSection>

      <FormSection>
        <Text textType="Subhead1">메모 및 추가 정보</Text>
        <FormItem>
          <Label>추가 정보</Label>
          <TextArea
            value={editedPatient.consultationNotes || ""}
            onChange={(e) => handleChange("consultationNotes", e.target.value)}
            placeholder="상담 내용을 입력하세요"
          />
        </FormItem>
        <FormItem>
          <Label>메모</Label>
          <TextArea
            value={editedPatient.memo || ""}
            onChange={(e) => handleChange("memo", e.target.value)}
            placeholder="기타 메모를 입력하세요"
          />
        </FormItem>
      </FormSection>

      <ButtonGroup>
        <Button
          buttonType="button"
          color="Negative"
          size="Small"
          type="Text"
          variant="Outlined"
          onClick={handleCancelConfirm}
        >
          취소
        </Button>
        <Button
          buttonType="button"
          color="Primary"
          size="Small"
          type="Text"
          variant="Contained"
          onClick={() => onSave(editedPatient)}
        >
          저장
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

export default React.memo(PatientInfoEditForm);
